import { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { ChartActionButton } from '../styles/components';
import { useT } from '../i18n';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  box-sizing: border-box;
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 180px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadow.lg};
  z-index: 100;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const MenuItem = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  background: transparent;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:first-of-type {
    border-radius: ${theme.borderRadius.md} ${theme.borderRadius.md} 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 ${theme.borderRadius.md} ${theme.borderRadius.md};
  }

  &:hover {
    background: ${theme.colors.background};
  }
`;

interface ExportDropdownProps {
  onExportPng: () => void;
  onExportSvg: () => void;
}

export function ExportDropdown({ onExportPng, onExportSvg }: ExportDropdownProps) {
  const m = useT();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleExportPng = () => {
    setIsOpen(false);
    onExportPng();
  };

  const handleExportSvg = () => {
    setIsOpen(false);
    onExportSvg();
  };

  return (
    <DropdownContainer ref={containerRef}>
      <ChartActionButton
        onClick={() => setIsOpen(!isOpen)}
        title={m.chart.exportChart}
        aria-label={m.chart.exportChart}
      >
        <span className="icon" data-icon="download" />
      </ChartActionButton>
      <DropdownMenu isOpen={isOpen}>
        <MenuItem onClick={handleExportPng}>
          <span className="icon" data-icon="image" />
          {m.chart.downloadPng}
        </MenuItem>
        <MenuItem onClick={handleExportSvg}>
          <span className="icon" data-icon="shape_line" />
          {m.chart.downloadSvg}
        </MenuItem>
      </DropdownMenu>
    </DropdownContainer>
  );
}
